'use client';

import Link from 'next/link';
import { ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackCtaClick } from '@/lib/analytics/track-cta-click';
import { cn } from '@/lib/utils';
import type { SanityCtaButton } from '@/sanity/lib/sections';

export function SectionCtaPair({
  primary,
  secondary,
  className,
  trackingLocation,
}: {
  primary?: SanityCtaButton | null;
  secondary?: SanityCtaButton | null;
  className?: string;
  trackingLocation?: string;
}) {
  if (!primary) return null;

  const location = trackingLocation ?? 'section_cta';

  return (
    <div className={cn('flex flex-col gap-2 pt-2 sm:flex-row', className)}>
      <Link
        href={primary.href}
        className="max-sm:w-full"
        data-lead-form-trigger={primary.isLeadForm ? 'true' : undefined}
        data-tracking-location={primary.isLeadForm ? `${location}_primary` : undefined}
        onClick={() =>
          trackCtaClick({
            location: `${location}_primary`,
            label: primary.label,
            href: primary.href,
            isLeadForm: primary.isLeadForm,
            variant: 'primary',
          })
        }
      >
        <Button
          size="lg"
          className="group h-10 rounded-full bg-[#948B7F] px-4 text-xs font-bold uppercase tracking-wider text-white transition-all duration-300 ease-in-out hover:bg-[#BFB7A1] hover:text-white max-sm:w-full"
        >
          <span>{primary.label}</span>
          <ArrowUpRight className="size-4 shrink-0 transition-transform duration-300 ease-in-out group-hover:rotate-45" />
        </Button>
      </Link>
      {secondary ? (
        <Link
          href={secondary.href}
          className="max-sm:w-full"
          data-lead-form-trigger={secondary.isLeadForm ? 'true' : undefined}
          data-tracking-location={secondary.isLeadForm ? `${location}_secondary` : undefined}
          onClick={() =>
            trackCtaClick({
              location: `${location}_secondary`,
              label: secondary.label,
              href: secondary.href,
              isLeadForm: secondary.isLeadForm,
              variant: 'secondary',
            })
          }
        >
          <Button
            variant="outline"
            size="lg"
            className="h-10 rounded-full px-4 text-xs font-bold uppercase tracking-wider max-sm:w-full"
          >
            {secondary.label}
          </Button>
        </Link>
      ) : null}
    </div>
  );
}

export function sectionCtaPairFromContent(
  showCTA: boolean | null | undefined,
  ctaButtons:
    | {
        primary?: SanityCtaButton | null;
        showSecondary?: boolean | null;
        secondary?: SanityCtaButton | null;
      }
    | null
    | undefined,
): { primary: SanityCtaButton | null; secondary: SanityCtaButton | null } {
  if (showCTA === false || !ctaButtons) {
    return { primary: null, secondary: null };
  }
  const primary = ctaButtons.primary ?? null;
  const secondary = ctaButtons.showSecondary === false ? null : (ctaButtons.secondary ?? null);
  return { primary, secondary };
}
